import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CoffeeRustAlertResponse,
  CoffeeRustAlertPaginatedResponse,
} from '../../models/coffee-rust-alert';
import { CoffeeRustAlertAdapter } from '../../adapters/coffee-rust-alert';
import { environment } from '@env/environment';

const BASE_URL =
  environment.apiBaseUrl + '/decision-support/sad-aviso/ferrugem';

/**
 * Serviço para acesso à API de alertas de ferrugem do café (Modelo Hinnah)
 */
@Injectable({
  providedIn: 'root',
})
export class CoffeeRustAlertApiService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Busca contagem de alertas ativos de ferrugem do café
   * Usa o endpoint paginado com size=1 para obter apenas o totalElements
   * @returns Observable com resposta contendo contagem de alertas
   */
  getActiveAlerts(): Observable<CoffeeRustAlertResponse> {
    const params = new HttpParams().set('page', '0').set('size', '1');
    const headers = new HttpHeaders({ 'X-Handle-Error-Locally': 'true' });
    return this.http
      .get<{ data: any }>(`${BASE_URL}`, { params, headers })
      .pipe(
        map((response: any) => {
          if (response?.data != null) {
            return CoffeeRustAlertAdapter.fromApiResponse(response.data);
          }
          return CoffeeRustAlertAdapter.fromApiResponse({});
        }),
        catchError(() => of(CoffeeRustAlertAdapter.fromApiResponse({}))),
      );
  }

  /**
   * Busca alertas ativos de ferrugem do café com paginação
   * @param page Número da página (padrão: 0)
   * @param size Tamanho da página (padrão: 10)
   * @param sortField Campo para ordenação (opcional, ex: noticeDate)
   * @param sortDirection Direção da ordenação - asc ou desc (opcional)
   * @param all Retornar todos os elementos na primeira página (padrão: false)
   * @returns Observable com resposta paginada
   */
  getActiveAlertsPaginated(
    page: number = 0,
    size: number = 10,
    sortField?: string,
    sortDirection?: string,
    all: boolean = false,
    modelId?: number | null,
  ): Observable<CoffeeRustAlertPaginatedResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('all', all.toString());

    if (modelId != null) {
      params = params.set('modelId', modelId.toString());
    }

    // A API espera dois parâmetros sort separados: ?sort=noticeDate&sort=desc
    if (sortField && sortDirection) {
      params = params.append('sort', sortField).append('sort', sortDirection);
    }

    return this.http
      .get<{ data: any }>(`${BASE_URL}`, {
        params,
        headers: new HttpHeaders({ 'X-Handle-Error-Locally': 'true' }),
      })
      .pipe(
        map((response: any) => {
          if (response?.data != null) {
            return CoffeeRustAlertAdapter.fromApiPaginatedResponse(
              response.data,
            );
          }
          return CoffeeRustAlertAdapter.fromApiPaginatedResponse({});
        }),
        catchError(() =>
          of(CoffeeRustAlertAdapter.fromApiPaginatedResponse({})),
        ),
      );
  }
}
